import { useEffect, useState } from "react";
import { useStore } from "zustand";

import { useRobot } from "../hooks/useRobot";
import { useRobotState } from "../hooks/useRobotState";
import type { OperatorProtocol } from "../modules/teleop-commands";
import styles from "./GoalPanel.module.scss";

export interface GoalPanelProps {
  protocol: OperatorProtocol;
  /** Session joined — commands can be sent. */
  enabled: boolean;
}

/** Goal event line stays visible this long. */
const EVENT_VISIBLE_MS = 8_000;

const EVENT_LABEL: Record<string, string> = {
  "goal-accepted": "принята",
  "goal-reached": "достигнута",
  "goal-aborted": "прервана",
  "goal-rejected": "отклонена",
  "goal-commanded": "отправлена",
};

const GOAL_LABELS: Record<string, string> = {
  "teleop-ready-vertical": "Подготовить руки",
  "teleop-rest-arms": "Отпустить руки",
};

/** Named poses reported by the robot; manual input is ignored during a transition. */
export function GoalPanel({ protocol, enabled }: GoalPanelProps) {
  const robotState = useRobotState();
  const { config } = useRobot();
  const goals = config?.robot?.goals ?? [];
  const availableGoals = goals.filter((name) => GOAL_LABELS[name]);
  const robotControlEnabled = config?.robot?.control_enabled === true;
  const restPoseConfirmed = config?.robot?.rest_pose_confirmed === true;
  const fullControl = config?.robot?.vr_profile === "full";
  // Primitive selectors: joint-state arrives at ≈30 Hz with a fresh goal object each time.
  const goalId = useStore(robotState.armState, (s) => s.joints?.goal?.goal_id ?? null);
  const goalName = useStore(robotState.armState, (s) => s.joints?.goal?.name ?? "");
  const progress = useStore(robotState.armState, (s) =>
    s.joints?.goal ? Math.round(s.joints.goal.progress * 100) : 0
  );
  const active = goalId !== null;
  const lastEvent = useStore(robotState.armState, (s) => s.lastGoalEvent);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const event = lastEvent && now - lastEvent.at < EVENT_VISIBLE_MS ? lastEvent : null;

  if (availableGoals.length === 0) return null;

  return (
    <section className={styles.panel} aria-label="Цель">
      <header className={styles.header}>
        <span className={styles.title}>Цель</span>
      </header>
      <div className={styles.row}>
        {availableGoals.map((name) => (
          <button
            key={name}
            type="button"
            className={styles.button}
            disabled={!enabled || !robotControlEnabled || active || (name === "teleop-rest-arms" && !restPoseConfirmed)}
            onClick={() => protocol.goal(name)}
            title={name === "teleop-rest-arms"
              ? restPoseConfirmed
                ? "Раскрыть схваты, плавно опустить руки и удерживать позу приводами"
                : "Ожидает сверки со штатной командой UnixAI"
              : fullControl
                ? "Раскрыть схваты, подготовить обе руки и плавно выровнять голову"
                : "Раскрыть схваты и плавно переместить руки в подготовленную позу"}
          >
            {GOAL_LABELS[name]}
          </button>
        ))}
      </div>
      <div className={styles.hint}>Обе позы раскрывают схваты. «Стоп» прерывает движение и удерживает руки.</div>
      {fullControl && <div className={styles.hint}>Подготовка также выравнивает голову. После завершения держите голову ровно и нажмите B в Quest.</div>}
      {active && (
        <div className={styles.status}>
          <span>
            Движение к «{GOAL_LABELS[goalName] ?? goalName}» — {progress}%
          </span>
          <div className={styles.progress} aria-hidden="true">
            <div className={styles.progressBar} style={{ width: `${progress}%` }} />
          </div>
          <span className={styles.hint}>Во время перехода ручное движение заблокировано</span>
        </div>
      )}
      {!active && event && (
        <div className={`${styles.status} ${event.level === "info" ? "" : styles.warn}`}>
          {event.code === "goal-commanded" ? "Команда" : "Цель"} {EVENT_LABEL[event.code] ?? event.code}
          {event.detail ? `: ${event.detail}` : ""}
        </div>
      )}
    </section>
  );
}
